import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  emptyPurchaseLineDraft,
  lineFigures,
  requisitionListedTotal,
  requisitionTaxTotal,
  requisitionTotal,
  type PurchaseLineDraft,
} from '../../utils/purchasingLineItems';
import { effectiveLineVendor } from '../../utils/purchaseLineVendors';
import { normalizePurchaseUrl } from '../../utils/purchasingLinks';
import PurchaseLinkedText from './PurchaseLinkedText';

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

const PurchaseLineItemsEditor: React.FC<{
  drafts: PurchaseLineDraft[];
  onChange: (drafts: PurchaseLineDraft[]) => void;
  salesTaxPct: number;
  /** Requisition vendor, used when a line does not name its own store. */
  headerVendor?: string;
  readOnly?: boolean;
}> = ({ drafts, onChange, salesTaxPct, headerVendor, readOnly }) => {
  const update = (index: number, patch: Partial<PurchaseLineDraft>) => {
    onChange(drafts.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const listed = requisitionListedTotal(
    drafts.map(row => ({
      quantity: Number(row.quantity) || 0,
      unitPrice: Number(row.unitPrice) || 0,
    })),
  );
  const tax = requisitionTaxTotal(
    drafts.map(row => ({
      quantity: Number(row.quantity) || 0,
      unitPrice: Number(row.unitPrice) || 0,
      taxable: row.taxable,
      taxRate: row.taxRate,
      taxAmount: row.taxAmount,
    })),
    salesTaxPct,
  );
  const total = requisitionTotal(
    drafts.map(row => ({
      quantity: Number(row.quantity) || 0,
      unitPrice: Number(row.unitPrice) || 0,
      taxable: row.taxable,
      taxRate: row.taxRate,
      taxAmount: row.taxAmount,
    })),
    salesTaxPct,
  );
  const ownRates = drafts
    .map(row => row.taxRate)
    .filter((rate): rate is number => typeof rate === 'number');
  const uniformRate = ownRates.length === drafts.length && ownRates.every(rate => rate === ownRates[0]);
  const taxLabel = uniformRate ? `Tax (${ownRates[0]}%)` : ownRates.length ? 'Tax' : `Tax (${salesTaxPct}%)`;

  return (
    <div className="po-lines">
      <div className="po-lines-head">
        <span>Line items</span>
        {!readOnly && (
          <button
            type="button"
            className="fin-secondary-btn"
            onClick={() => onChange([...drafts, emptyPurchaseLineDraft()])}
          >
            <Plus size={13} /> Add line
          </button>
        )}
      </div>
      <div className="po-lines-table-wrap">
        <table className="po-lines-table">
          <colgroup>
            <col className="po-col-desc" />
            <col className="po-col-qty" />
            <col className="po-col-price" />
            <col className="po-col-tax" />
            <col className="po-col-amount" />
            {!readOnly && <col className="po-col-remove" />}
          </colgroup>
          <thead>
            <tr>
              <th className="po-col-desc">Description</th>
              <th className="po-col-qty">Qty</th>
              <th className="po-col-price">Listed</th>
              <th className="po-col-tax">Tax</th>
              <th className="po-col-amount">Amount</th>
              {!readOnly && <th className="po-col-remove" />}
            </tr>
          </thead>
          <tbody>
            {drafts.map((row, index) => {
              const qty = Number(row.quantity);
              const price = Number(row.unitPrice);
              const figures = lineFigures(
                {
                  quantity: qty,
                  unitPrice: price,
                  taxable: row.taxable,
                  taxRate: row.taxRate,
                  taxAmount: row.taxAmount,
                },
                salesTaxPct,
              );
              let readOnlyTax = '—';
              if (typeof row.taxRate === 'number') readOnlyTax = money(figures.tax);
              else if (row.taxable) readOnlyTax = `${salesTaxPct}%`;
              return (
                <tr key={row.key}>
                  <td className="po-col-desc">
                    {readOnly ? (
                      <>
                        <PurchaseLinkedText text={row.description} />
                        {effectiveLineVendor(row, headerVendor) ? (
                          <div className="po-line-vendor">{effectiveLineVendor(row, headerVendor)}</div>
                        ) : null}
                        {row.poNumber ? <div className="po-line-po">{row.poNumber}</div> : null}
                        {row.productUrl && normalizePurchaseUrl(row.productUrl) ? (
                          <a
                            className="po-line-product-link"
                            href={normalizePurchaseUrl(row.productUrl) || undefined}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {row.productLabel?.trim() || 'Product link'}
                          </a>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <input
                          className="fin-modal-input"
                          value={row.description}
                          onChange={e => update(index, { description: e.target.value })}
                          placeholder="Item or service"
                        />
                        <input
                          className="fin-modal-input po-line-vendor-input"
                          value={row.vendorName || ''}
                          onChange={e =>
                            // CRM #73: changing store must drop the prior per-line PO stamp
                            // so it cannot collide with another issued purchase order.
                            update(index, { vendorName: e.target.value, poNumber: undefined })
                          }
                          placeholder={headerVendor?.trim() ? `Vendor (uses ${headerVendor.trim()})` : 'Vendor for this line'}
                        />
                      </>
                    )}
                  </td>
                  <td className="po-col-qty">
                    {readOnly ? (
                      row.quantity
                    ) : (
                      <input
                        className="fin-modal-input po-lines-qty"
                        type="number"
                        min={0}
                        step="1"
                        value={row.quantity}
                        onChange={e => update(index, { quantity: e.target.value })}
                      />
                    )}
                  </td>
                  <td className="po-col-price">
                    {readOnly ? (
                      money(price || 0)
                    ) : (
                      <input
                        className="fin-modal-input po-lines-price"
                        type="number"
                        min={0}
                        step="0.01"
                        value={row.unitPrice}
                        onChange={e => update(index, { unitPrice: e.target.value })}
                        placeholder="0.00"
                      />
                    )}
                  </td>
                  <td className="po-col-tax">
                    {readOnly ? (
                      readOnlyTax
                    ) : (
                      <label className="po-tax-toggle">
                        <input
                          type="checkbox"
                          checked={row.taxable}
                          onChange={e => update(index, { taxable: e.target.checked })}
                        />
                        <span>{salesTaxPct}%</span>
                      </label>
                    )}
                  </td>
                  <td className="po-col-amount">{money(figures.amount)}</td>
                  {!readOnly && (
                    <td className="po-col-remove">
                      <button
                        type="button"
                        className="po-lines-remove"
                        aria-label="Remove line"
                        disabled={drafts.length <= 1}
                        onClick={() => onChange(drafts.filter((_, i) => i !== index))}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4} className="po-lines-total-label">
                Listed
              </td>
              <td className="po-col-amount">{money(listed)}</td>
              {!readOnly && <td className="po-col-remove" />}
            </tr>
            <tr>
              <td colSpan={4} className="po-lines-total-label">
                {taxLabel}
              </td>
              <td className="po-col-amount">{money(tax)}</td>
              {!readOnly && <td className="po-col-remove" />}
            </tr>
            <tr>
              <td colSpan={4} className="po-lines-total-label">
                Total
              </td>
              <td className="po-col-amount po-lines-total">{money(total)}</td>
              {!readOnly && <td className="po-col-remove" />}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default PurchaseLineItemsEditor;
